import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Viewport sizing shared by the item-master result grids — the Handsontable
 * AS/400 grids and the catalog's Gently results table.
 *
 * Both have the same problem. Handsontable freezes the column header with its
 * `ht_clone_top` overlay, and GentlyTable freezes its `<thead>` with
 * `position: sticky`; in both cases the header only stays put when the grid owns
 * a *bounded* scroll viewport. Left to size themselves they render every row at
 * full height, vertical scrolling falls to the page, and the header scrolls away
 * with it — along with everything else the page renders above the rows.
 *
 * So the grid gets an explicit height: the room left on screen below it (for
 * Handsontable, capped at its own *measured* content — see
 * {@link measuredContentHeight}). A short page stays snug with no dead space, and
 * a full one fills the viewport with an internal scroller under the frozen
 * header.
 */

/** Row height the grids are configured with; the floor a row renders at. */
export const GRID_ROW_HEIGHT = 28;

// Slightly over the rendered column-header height, so a table short enough to
// fit entirely errs toward a few blank pixels rather than a stray scrollbar.
const GRID_HEADER_HEIGHT = 32;

// Never collapse past this, however little room the measurement reports — a
// grid too short to show a row or two is worse than one that overflows.
const MIN_GRID_HEIGHT = 240;

// Breathing room over the measured content height, so the height we settle on
// definitely clears the content: a grid that lands one sub-pixel short brings
// its scrollbar back, reads as overflowing on the next measurement, and
// oscillates between filling the viewport and hugging its rows.
const CONTENT_FIT_SLACK = 2;

/**
 * Whether the element is currently not laid out at all — `display: none` on it
 * or on an ancestor.
 *
 * Every measurement below reads zeroes from such an element, and zeroes are not
 * "no room on screen", they are "no answer". Acting on them would collapse the
 * grid to nothing, or (worse) read as `available <= 0` and paint it at its
 * row-count estimate — hundreds of thousands of pixels for a large table. So the
 * measurements hold their last real value until the grid is back on screen.
 *
 * This is what lets a caller hide the grid (while search results take its place)
 * instead of unmounting it: remounting Handsontable over a whole table is slow
 * enough to feel like a freeze, and it has to survive being hidden to be worth
 * keeping mounted.
 */
function isUnrendered(element: HTMLElement): boolean {
  // Walks ancestors rather than asking `offsetParent`, which is null in a test
  // environment with no layout engine and would disable measuring entirely, and
  // rather than reading the element's own `display`, which misses the case that
  // matters — an ancestor doing the hiding. `getComputedStyle` per ancestor is
  // the same order of cost as `scrollParent`, which already walks the same chain.
  for (
    let node: HTMLElement | null = element;
    node;
    node = node.parentElement
  ) {
    if (getComputedStyle(node).display === 'none') return true;
  }
  return false;
}

/** The nearest scrollable ancestor — the viewport the grid has to fit inside. */
function scrollParent(element: HTMLElement): HTMLElement {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return document.documentElement;
}

/**
 * Height of everything the scrollport renders after `wrapper` — the pager, the
 * page's bottom padding — measured from the laid-out content.
 *
 * Deliberately NOT derived from `scroller.scrollHeight`. `scrollHeight` is
 * clamped to `clientHeight`, so on a page that doesn't fill the viewport it
 * reports no trailing content at all, and `availableGridHeight` collapses to
 * `clientHeight - scrollHeight + wrapperHeight` = the wrapper's *current* height.
 * That fixed point is invisible while a grid is shrinking to fit (it converges
 * from above, having first painted too tall) but it means a grid can never GROW
 * into free space: it keeps whatever height it happened to paint at first — for
 * the catalog table, the height of its loading state.
 */
function trailingHeight(wrapper: HTMLElement, scroller: HTMLElement): number {
  const last = scroller.lastElementChild;
  if (!last) return 0;
  // The scrollport's own bottom padding sits below its last child's box.
  const paddingBottom =
    Number.parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
  const contentBottom = last.getBoundingClientRect().bottom + paddingBottom;
  return Math.max(0, contentBottom - wrapper.getBoundingClientRect().bottom);
}

/**
 * Room on screen for the grid: the scrollport's visible height, less the grid's
 * own offset within it, less whatever renders after the grid.
 *
 * Neither term depends on the grid's own height — resizing the grid moves the
 * wrapper's bottom edge and the content bottom by the same amount, leaving
 * `trailingHeight` unchanged — which is what makes this converge in one pass
 * instead of drifting as the ResizeObserver re-measures.
 */
function availableGridHeight(wrapper: HTMLElement): number {
  const scroller = scrollParent(wrapper);
  const offsetTop =
    wrapper.getBoundingClientRect().top -
    scroller.getBoundingClientRect().top +
    scroller.scrollTop;
  return scroller.clientHeight - offsetTop - trailingHeight(wrapper, scroller);
}

/**
 * Room on screen for the element the returned ref is attached to — 0 until the
 * first measurement lands. Re-measures on viewport resize and on any layout
 * change above or below it (the filters panel opening, a toggle row wrapping,
 * the staging bar appearing).
 *
 * Attach the ref to the wrapper around the scrolling box ONLY. Anything that
 * renders after it — the pager especially — must sit outside that wrapper, where
 * `trailingHeight` accounts for it.
 */
export function useAvailableViewportHeight(): [
  RefObject<HTMLDivElement | null>,
  number,
] {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState(0);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const measure = () => {
      if (isUnrendered(wrapper)) return;
      const next = availableGridHeight(wrapper);
      // Sub-pixel jitter would otherwise re-render on every observer tick.
      setAvailable((previous) =>
        Math.abs(previous - next) < 1 ? previous : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    // `document.body` alone only catches viewport resizes: the app shell pins it
    // to the viewport (`h-svh overflow-hidden` on the sidebar inset), so it never
    // reports a page-internal layout change. The scrollport's content element
    // does — that's what grows when the header above the grid does.
    observer.observe(document.body);
    const content = scrollParent(wrapper).lastElementChild;
    if (content) observer.observe(content);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return [wrapperRef, available];
}

/**
 * Handsontable's own content height, read off its DOM: the height at which the
 * current page of rows shows in full, with the horizontal scrollbar's own strip
 * accounted for. `undefined` while the rendered table is taller than the box it
 * sits in — the rows are then virtualized, so the DOM holds the rendered window
 * rather than the whole page and can't say how tall that page is.
 *
 * Measured rather than computed from the row count, because `rowHeights` is a
 * floor and not a promise: Handsontable wraps cell text (`white-space:
 * pre-wrap`), so a row of wrapped descriptions renders two or three times
 * GRID_ROW_HEIGHT tall. Capping the grid at `rowCount * GRID_ROW_HEIGHT` left a
 * page whose rows all wrapped — one division's worth of items, say — pinned to a
 * fraction of the viewport, scrolling internally with the rest of the page blank
 * below it (GEN-2351), while a page with enough rows to pass the cap filled the
 * screen as intended.
 */
function measuredContentHeight(wrapper: HTMLElement): number | undefined {
  // Walkontable's master overlay: `.wtHolder` is the scrolling box the grid's
  // height lands on, `.htCore` the table of rendered rows inside it. The clone
  // overlays (frozen header, frozen columns) have the same parts, hence the
  // `.ht_master` scope.
  const holder = wrapper.querySelector<HTMLElement>('.ht_master .wtHolder');
  const table = wrapper.querySelector<HTMLElement>('.ht_master .htCore');
  if (!holder || !table) return undefined;
  // Taller than the box means rows are being virtualized away — measuring the
  // rendered window would cap the grid at roughly its current height. Anything
  // that fits is the whole page: Handsontable always renders enough rows to
  // cover the box, so a table short of it has nothing left to render.
  if (table.offsetHeight > holder.clientHeight) return undefined;
  // The horizontal scrollbar eats into the box's client height, so the content
  // needs that strip on top of it — otherwise shrinking to fit vertically hands
  // the vertical scrollbar straight back.
  const scrollbarHeight = holder.offsetHeight - holder.clientHeight;
  return table.offsetHeight + scrollbarHeight + CONTENT_FIT_SLACK;
}

/** The boxes {@link measuredContentHeight} reads, in a stable order. */
function measuredParts(wrapper: HTMLElement): HTMLElement[] {
  const gridBoxes = ['.ht_master .wtHolder', '.ht_master .htCore'].map(
    (selector) => wrapper.querySelector<HTMLElement>(selector),
  );
  return [
    wrapper,
    ...gridBoxes.filter((box): box is HTMLElement => box !== null),
  ];
}

/**
 * {@link measuredContentHeight} for the grid inside `wrapper`, re-measured
 * whenever a new page of rows lands, the room on screen changes, or the grid
 * relays itself out.
 */
function useMeasuredContentHeight(
  wrapperRef: RefObject<HTMLElement | null>,
): number | undefined {
  const [contentHeight, setContentHeight] = useState<number | undefined>(
    undefined,
  );
  const observer = useRef<ResizeObserver | null>(null);
  const observed = useRef<HTMLElement[]>([]);

  // Deliberately no dependency list. This has to re-measure after every render
  // — the rows and the room on screen both arrive that way — and keep its
  // observer pointed at the boxes Handsontable owns *now*: a grid that remounts
  // (PricelistCsvGrid keys its table on the data version) builds new ones, and
  // an observer left on the detached originals would never fire again.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const measure = () => {
      if (isUnrendered(wrapper)) return;
      const next = measuredContentHeight(wrapper);
      // Sub-pixel jitter would otherwise re-render on every observer tick.
      setContentHeight((previous) =>
        previous !== undefined &&
        next !== undefined &&
        Math.abs(previous - next) < 1
          ? previous
          : next,
      );
    };
    // The grid's DOM exists by now: Handsontable builds it from the child
    // <HotTable>'s own effect, which React runs before this one.
    measure();

    const parts = measuredParts(wrapper);
    const unchanged =
      parts.length === observed.current.length &&
      parts.every((part, index) => part === observed.current[index]);
    if (unchanged) return;
    observer.current?.disconnect();
    // Catches the relayouts React never sees — a column dragged wider, so the
    // text that set the row heights rewraps.
    observer.current = new ResizeObserver(measure);
    for (const part of parts) observer.current.observe(part);
    observed.current = parts;
  });

  // Cleanup has to undo the binding, not just stop it: Strict Mode runs setup →
  // cleanup → setup, and leaving `observed` populated would make the second
  // setup consider its (disconnected) observer still current and skip rebinding.
  useEffect(
    () => () => {
      observer.current?.disconnect();
      observer.current = null;
      observed.current = [];
    },
    [],
  );

  return contentHeight;
}

/**
 * The height to hand Handsontable: its measured content height while that fits
 * on screen, otherwise all the room there is.
 *
 * Exported for the tests; {@link useSpreadsheetGridHeight} is what the grids use.
 */
export function spreadsheetGridHeight({
  available,
  contentHeight,
  estimatedContentHeight,
  viewportHeight = Number.POSITIVE_INFINITY,
  maxHeight,
}: {
  /** Room on screen for the grid, 0 before the first measurement. */
  available: number;
  /** The grid's measured content height, undefined while it overflows. */
  contentHeight: number | undefined;
  /** What the row count alone predicts, for the pre-measurement paint. */
  estimatedContentHeight: number;
  /**
   * The window's height, bounding that estimate. Handsontable renders every
   * row that fits its height, so a big pricelist's estimate — 560,032px for
   * 20,000 rows — made the first paint render the whole table and froze the
   * page for seconds (GEN-2645). A measured grid never outgrows the window
   * anyway. One that is never measured — hidden, or below the fold — now
   * settles at the window's height with its own scroller, not the table's.
   */
  viewportHeight?: number;
  /**
   * Upper bound. A long table then scrolls inside the grid instead of
   * stretching the page — the pricelist case page uses this so History and
   * Discussion stay on the page scroll, under the table.
   */
  maxHeight?: number;
}): number {
  // Before the first measurement, fall back to the row-count estimate, bounded
  // by the window — one paint at roughly the right height, then it settles on
  // the measured one.
  const fitted =
    available <= 0
      ? Math.min(estimatedContentHeight, viewportHeight)
      : Math.min(
          contentHeight ?? Number.POSITIVE_INFINITY,
          Math.max(MIN_GRID_HEIGHT, available),
        );
  return maxHeight === undefined ? fitted : Math.min(fitted, maxHeight);
}

/**
 * The height to hand Handsontable, plus the ref for the wrapper it's measured
 * from: its own content, capped at the room on screen.
 */
export function useSpreadsheetGridHeight(
  rowCount: number,
  maxHeight?: number,
): [RefObject<HTMLDivElement | null>, number] {
  const [wrapperRef, available] = useAvailableViewportHeight();
  const contentHeight = useMeasuredContentHeight(wrapperRef);

  return [
    wrapperRef,
    spreadsheetGridHeight({
      available,
      contentHeight,
      estimatedContentHeight: rowCount * GRID_ROW_HEIGHT + GRID_HEADER_HEIGHT,
      viewportHeight: window.innerHeight,
      maxHeight,
    }),
  ];
}

/**
 * The `maxHeight` for a scrolling results table, plus the ref for the wrapper
 * it's measured from. Unlike the Handsontable variant this doesn't cap at
 * content height — the table's own rows size themselves, so a short page just
 * doesn't reach the cap and never scrolls.
 *
 * `undefined` until the first measurement, which leaves the table unbounded for
 * one paint rather than collapsing it to nothing.
 */
export function useResultsTableMaxHeight(): [
  RefObject<HTMLDivElement | null>,
  number | undefined,
] {
  const [wrapperRef, available] = useAvailableViewportHeight();
  return [
    wrapperRef,
    available > 0 ? Math.max(MIN_GRID_HEIGHT, available) : undefined,
  ];
}
